import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WalletHeroCard from "../components/WalletHeroCard";
import IdentityVerificationDialog from "../components/IdentityVerificationDialog";
import VerifiedName from "../components/VerifiedName";
import { Sparkles, Trophy, ShieldCheck, Zap, Crown } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";
import { BonusBadge } from "../lib/gameBonus";
import useLiveGames from "../lib/useLiveGames";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/f281174e-d5b3-4f4a-816c-f5cd8faf2e5b/images/f1ef492cf8817373a59291f0b591720b32024852b5a1267c3b274a73031044fe.png";

export default function Landing() {
  const { user, refreshUser } = useAuth();
  const games = useLiveGames().slice(0, 3);
  const [leaders, setLeaders] = useState([]);
  const [gameBonuses, setGameBonuses] = useState({});
  const [identityOpen, setIdentityOpen] = useState(false);
  const isAuthed = user && user !== false;
  const primaryCta = isAuthed
    ? { to: "/dashboard", label: "Open your dashboard", testId: "hero-dashboard-button" }
    : { to: "/signup", label: "Create your account", testId: "hero-signup-button" };

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setLeaders(data.slice(0, 6))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthed) { setGameBonuses({}); return; }
    api.get("/me/game-bonuses").then(({ data }) => setGameBonuses(data.bonuses || {})).catch(() => setGameBonuses({}));
  }, [isAuthed]);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#030705] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#030705]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pb-28 sm:pt-16 md:px-10 md:pb-36 md:pt-28">
          <div className="grid min-w-0 gap-10 md:grid-cols-12 lg:gap-12 items-start">
            {/* LEFT — headline + CTA */}
            <div className="min-w-0 md:col-span-7">
              <div className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#0B2115]/60 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] backdrop-blur fade-up sm:mb-8 sm:text-xs" data-testid="hero-badge">
                <Sparkles className="w-3.5 h-3.5" /> Now in early access · 2026
              </div>
              <h1 className="max-w-full break-words font-display text-4xl font-black leading-[1.02] tracking-tight fade-up sm:text-5xl md:text-7xl md:leading-[0.95] lg:text-8xl">
                Free to play.<br />
                <span className="gold-text">Real rewards.</span><br />
                One account.
              </h1>
              <p className="mt-6 max-w-full text-base leading-relaxed text-white/70 fade-up sm:mt-8 sm:max-w-xl sm:text-lg md:text-xl" style={{ animationDelay: "120ms" }}>
                America's premium social casino — slots, poker, fishing and more. Free to play, with real prizes through our sweepstakes model.
              </p>
              <div className="mt-8 flex flex-col gap-3 fade-up min-[430px]:flex-row min-[430px]:flex-wrap sm:mt-10" style={{ animationDelay: "240ms" }}>
                <Link to={primaryCta.to} data-testid={primaryCta.testId} className="inline-flex w-full items-center justify-center rounded-full bg-[#D4AF37] px-6 py-4 text-center font-bold text-black shadow-xl shadow-black/40 transition-all hover:-translate-y-1 hover:bg-[#F1C40F] min-[430px]:w-auto sm:px-8">
                  {primaryCta.label}
                </Link>
                <Link to="/games" data-testid="hero-explore-button" className="inline-flex w-full items-center justify-center rounded-full border border-white/20 px-6 py-4 text-center transition-all hover:bg-white/5 min-[430px]:w-auto sm:px-8">
                  Explore games →
                </Link>
              </div>

              <div className="mt-10 grid gap-3 text-sm text-white/60 fade-up min-[380px]:grid-cols-2 sm:mt-12 sm:flex sm:flex-wrap sm:gap-x-10 sm:gap-y-4" style={{ animationDelay: "360ms" }}>
                <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#D4AF37]" /> Instant bonuses</span>
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Host-verified</span>
                <span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D4AF37]" /> Weekly leaderboards</span>
              </div>
            </div>

            {/* RIGHT — Live leaderboard */}
            <aside className="min-w-0 md:col-span-5 md:mt-4 fade-up" style={{ animationDelay: "180ms" }} data-testid="hero-leaderboard">
              <div className="max-w-full rounded-2xl border border-[#D4AF37]/30 bg-black/50 p-4 shadow-2xl shadow-black/60 backdrop-blur-2xl sm:p-7">
                <div className="mb-5 flex min-w-0 items-center justify-between gap-3 sm:mb-6">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Crown className="w-5 h-5 text-[#D4AF37]" />
                    <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] sm:text-sm sm:tracking-[0.25em]">Top players</span>
                  </div>
                  <Link to="/leaderboard" className="shrink-0 text-xs text-white/50 hover:text-white sm:text-sm" data-testid="hero-leaderboard-see-all">
                    See all →
                  </Link>
                </div>

                {leaders.length === 0 && (
                  <div className="py-12 text-center text-base text-white/40">
                    No players yet — be the first to spin.
                  </div>
                )}

                <ul className="space-y-2.5">
                  {leaders.map((u, i) => (
                    <li
                      key={u.username + i}
                      data-testid={`hero-leader-${i}`}
                      className={`flex min-w-0 items-center justify-between gap-3 rounded-xl border px-3 py-3 transition-colors sm:px-4 sm:py-3.5 ${
                        i === 0
                          ? "bg-gradient-to-r from-[#D4AF37]/20 to-transparent border-[#D4AF37]/40"
                          : "bg-white/[0.02] border-white/5 hover:bg-white/5"
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                        <span className={`w-8 shrink-0 text-center font-display text-xl font-black sm:w-10 sm:text-2xl ${i < 3 ? "gold-text" : "text-white/30"}`}>
                          #{i + 1}
                        </span>
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B2115] to-[#030705] font-display text-sm font-bold sm:h-11 sm:w-11 sm:text-base">
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                        <VerifiedName user={u} name={u.username} className="min-w-0 text-sm font-semibold sm:text-base" />
                      </div>
                      <span className="shrink-0 text-right">
                        <span className="block tabular-nums text-sm font-bold text-white sm:text-base">{leaderMoney(u)}</span>
                        <span className="block text-[10px] uppercase tracking-[0.16em] text-white/35">weekly</span>
                        {u.reward && (
                          <span className="mt-1 inline-flex rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5 text-[10px] font-black text-[#F8E08E]">
                            {u.reward.label}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {leaders.length > 0 && (
                  <div className="mt-6 flex min-w-0 items-center justify-between gap-3 border-t border-white/10 pt-5 text-xs text-white/50 sm:text-sm">
                    <span>Live · updated each spin</span>
                    <Link to={isAuthed ? "/leaderboard" : "/signup"} className="text-[#D4AF37] hover:underline font-semibold">
                      {isAuthed ? "View board →" : "Join the board →"}
                    </Link>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {isAuthed && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 -mt-14 pb-12">
          <WalletHeroCard
            user={user}
            onBalanceChange={() => refreshUser?.()}
            onKycRequired={() => setIdentityOpen(true)}
          />
        </section>
      )}

      {/* FEATURED GAMES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-28">
        <div className="flex items-end justify-between mb-6 md:mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Featured slots</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Spin the gilded reels</h2>
          </div>
          <Link to="/games" className="text-sm text-white/60 hover:text-[#D4AF37]" data-testid="featured-see-all">See all games →</Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 md:gap-6">
          {games.map((g, i) => (
            <Link
              key={g.id}
              to={`/games/${g.id}`}
              data-testid={`featured-game-${i}`}
              className="group relative flex aspect-[5/4] min-h-[9.5rem] flex-col justify-end overflow-hidden rounded-2xl border border-white/5 bg-[#0A130E] md:aspect-[4/5]"
            >
              <img src={g.image_url} alt={g.name} className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105 md:group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
              <BonusBadge game={g} bonuses={gameBonuses} className="absolute left-3 top-3 z-10 md:left-4 md:top-4" />
              <div className="relative p-3 sm:p-4 md:p-6">
                <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] md:mb-2 md:text-xs">{g.category}</div>
                <h3 className="font-display text-lg font-bold leading-tight md:mb-1 md:text-2xl">{g.name}</h3>
                <p className="mt-1 hidden text-sm text-white/60 line-clamp-2 sm:block">{g.description}</p>
                <div className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-[#D4AF37] transition-all group-hover:gap-3 md:mt-4 md:text-sm">
                  Play now →
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">How {siteConfig.name} works</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight">Three steps. Real prizes.</h2>
            <p className="mt-6 text-white/60 leading-relaxed">Our sweepstakes model means you play for free with dollar balances, and the rewards are real. No purchase necessary.</p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
            {[
              { n: "01", t: isAuthed ? "Choose a game" : "Create your account", d: isAuthed ? "Pick your favorite game and manage play from one account." : "Sign up free in 30 seconds — no credit card needed." },
              { n: "02", t: "Verify your phone", d: "Confirm your number to secure your account and claim eligible phone Freeplay." },
              { n: "03", t: "Play & redeem", d: "Spin slots, climb the board, redeem real-world rewards." },
            ].map((s) => (
              <div key={s.n} className="rounded-xl border border-white/5 bg-[#0A130E] p-6">
                <div className="font-display text-3xl gold-text mb-3">{s.n}</div>
                <h4 className="font-semibold mb-2">{s.t}</h4>
                <p className="text-sm text-white/60">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B2115] via-[#0A130E] to-[#030705] p-12 md:p-20 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter">
            Your reels are <span className="gold-text">waiting.</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">
            {isAuthed ? "Your account is ready. Jump back into the games and keep your play moving." : "Free to play. Real to win. Join thousands already spinning the gilded reels."}
          </p>
          <Link to={isAuthed ? "/games" : "/signup"} data-testid={isAuthed ? "cta-games-button" : "cta-signup-button"} className="inline-block mt-10 px-10 py-4 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-1 hover:bg-[#F1C40F] transition-all">
            {isAuthed ? "Browse games" : "Start playing — it's free"}
          </Link>
        </div>
      </section>

      <Footer />
      <IdentityVerificationDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        onSubmitted={() => refreshUser?.()}
        user={user}
      />
    </div>
  );
}

function leaderMoney(row) {
  const coins = Number(row?.total_load_coins ?? row?.coins ?? 0);
  return `$${(coins / 1000).toFixed(2)}`;
}
